const plain = value => String(value || '').replace(/<[^>]*>/g, ' ').replace(/&quot;|&#34;/g, '"').replace(/&nbsp;/g, ' ').replace(/&apos;|&#39;/g, "'").replace(/[״“”]/g, '"').replace(/[׳’]/g, "'");
const clean = value => plain(value).replace(/^[\s()]+|[).,:;\s]+$/g, '').trim();
const normalize = value => clean(value).replace(/_/g, ' ').toLowerCase();
const numeralValues = { א:1, ב:2, ג:3, ד:4, ה:5, ו:6, ז:7, ח:8, ט:9, י:10, כ:20, ל:30, מ:40, נ:50, ס:60, ע:70, פ:80, צ:90, ק:100, ר:200, ש:300, ת:400 };
const numeral = value => /^\d+$/.test(value) ? Number(value) : [...String(value || '')].reduce((sum, letter) => sum + (numeralValues[letter] || 0), 0);
export const isInternalSourceId = value => /^(?:\d+_[\d*]+|dach:[\w:.-]+|\d{7,}:(?:sicha|maamar|letter|seif):[\w:.-]+)$/i.test(String(value || '').trim());
const parashaStarts = [
  [/\bBereishit\b|\bBereshit\b|בראשית/i, 'Genesis 1:1'],
  [/\bNoach\b|\bNoah\b|(?:^|[ ,])נח(?:[ ,]|$)/i, 'Genesis 6:9'],
  [/\bLech Lecha\b|לך לך/i, 'Genesis 12:1'],
];
function parashaStart(reader) {
  const context = `${reader?.parsha || ''} ${reader?._liveRef || ''} ${reader?.ref || ''}`;
  return parashaStarts.find(([pattern]) => pattern.test(context))?.[1] || '';
}
function anchorText(source, reader) {
  return plain(reader?.segments?.find(segment => segment.ref === source.anchorRef || segment.id === source.anchorRef)?.he || '');
}

// “On the verse” in Shir HaShirim Rabbah refers to the preceding verse note
// in the same paragraph, never to the beginning of the entire book.
export function contextualMidrashRef(source, sources) {
  if (!/שהש[״"]ר\s+עה[״"]פ/.test(source?.he || source?.en || '')) return '';
  const previous = sources.filter(item => item.footnote && item.anchorRef === source.anchorRef && item.n < source.n).sort((a,b) => b.n-a.n)[0];
  const verse = String(previous?.he || previous?.en || '').match(/שה[״"]ש\s+([א-ת״"׳']+),\s*([א-ת״"׳']+)/);
  if (!verse) return '';
  const chapter = numeral(verse[1]), segment = numeral(verse[2]);
  return chapter > 0 && chapter <= 8 && segment > 0 ? `Shir HaShirim Rabbah ${chapter}:${segment}` : '';
}

export function canonicalCitationRefs(source, reader = {}) {
  const text = clean([source.title, source.he, source.en, isInternalSourceId(source.srcRef) ? '' : source.srcRef].filter(Boolean).join(' '));
  const refs = [];
  const tanakh = { בראשית:'Genesis', שמות:'Exodus', ויקרא:'Leviticus', במדבר:'Numbers', דברים:'Deuteronomy', יהושע:'Joshua', שופטים:'Judges', ישעיהו:'Isaiah', ישעיה:'Isaiah', ירמיהו:'Jeremiah', ירמיה:'Jeremiah', יחזקאל:'Ezekiel', הושע:'Hosea', יואל:'Joel', עמוס:'Amos', עובדיה:'Obadiah', יונה:'Jonah', מיכה:'Micah', נחום:'Nahum', חבקוק:'Habakkuk', צפניה:'Zephaniah', חגי:'Haggai', זכריה:'Zechariah', מלאכי:'Malachi', תהלים:'Psalms', תהילים:'Psalms', משלי:'Proverbs', איוב:'Job', 'שיר השירים':'Song of Songs', רות:'Ruth', איכה:'Lamentations', קהלת:'Ecclesiastes', אסתר:'Esther', דניאל:'Daniel', עזרא:'Ezra', נחמיה:'Nehemiah' };
  const parshiyos = {
    Genesis: ['נח','לך לך','וירא','חיי שרה','תולדות','ויצא','וישלח','וישב','מקץ','ויגש','ויחי'],
    Exodus: ['וארא','בא','בשלח','יתרו','משפטים','תרומה','תצוה','תצווה','כי תשא','ויקהל','פקודי'],
    Leviticus: ['צו','שמיני','תזריע','מצורע','אחרי','אחרי מות','קדושים','אמור','בהר','בחוקותי'],
    Numbers: ['נשא','בהעלותך','שלח','קרח','חקת','חוקת','בלק','פינחס','פנחס','מטות','מסעי'],
    Deuteronomy: ['ואתחנן','עקב','ראה','כי תצא','כי תבוא','נצבים','ניצבים','וילך','האזינו','וזאת הברכה'],
  };
  for (const [book, names] of Object.entries(parshiyos)) for (const name of names) tanakh[name] = book;
  const escape = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const biblePattern = new RegExp(`(?:^|[\\s(])(${Object.keys(tanakh).sort((a,b) => b.length-a.length).map(escape).join('|')})\\s+(?:פ(?:רק)?["']?\\s*)?([א-ת"']{1,7}|\\d+)\\s*[,.:]\\s*(?:פסוק\\s*)?([א-ת"']{1,7}|\\d+)(?:\\s*[-–]\\s*([א-ת"']{1,7}|\\d+))?(?=$|[\\s).,;:])`, 'g');
  for (const match of text.matchAll(biblePattern)) {
    const chapter = numeral(match[2]), verse = numeral(match[3]);
    const end = match[4] ? numeral(match[4]) : verse;
    if (chapter > 0 && chapter <= 150 && verse > 0 && end >= verse && end <= 200) refs.push(`${tanakh[match[1]]} ${chapter}:${verse}${end > verse ? `-${end}` : ''}`);
  }
  const englishBooks = { Hosea:'Hosea', Hoshea:'Hosea', Isaiah:'Isaiah', Yeshayahu:'Isaiah', Jeremiah:'Jeremiah', Ezekiel:'Ezekiel', Genesis:'Genesis', Exodus:'Exodus', Leviticus:'Leviticus', Numbers:'Numbers', Deuteronomy:'Deuteronomy', Psalms:'Psalms', Tehillim:'Psalms', Proverbs:'Proverbs', 'Song of Songs':'Song of Songs', Ecclesiastes:'Ecclesiastes', Job:'Job', Daniel:'Daniel', Joel:'Joel', Amos:'Amos', Obadiah:'Obadiah', Jonah:'Jonah', Micah:'Micah', Nahum:'Nahum', Habakkuk:'Habakkuk', Zephaniah:'Zephaniah', Haggai:'Haggai', Zechariah:'Zechariah', Malachi:'Malachi' };
  const englishPattern = new RegExp(`\\b(${Object.keys(englishBooks).join('|')})[,\\s]+(?:ch(?:apter)?\\.?\\s*)?(\\d{1,3})\\s*[:,.]\\s*(\\d{1,3})(?:\\s*[-–]\\s*(\\d{1,3}))?(?=$|[^\\d])`, 'gi');
  for (const match of text.matchAll(englishPattern)) {
    const book = Object.keys(englishBooks).find(key => key.toLowerCase() === match[1].toLowerCase());
    const startVerse = Number(match[3]), endVerse = Number(match[4] || match[3]);
    if (Number(match[2]) > 0 && startVerse > 0 && endVerse >= startVerse) refs.push(`${englishBooks[book]} ${Number(match[2])}:${startVerse}${endVerse > startVerse ? `-${endVerse}` : ''}`);
  }
  const start = parashaStart(reader);
  if (/ריש\s+פרשתנו|ר"פ\s*פרשתנו/.test(text) && start) refs.push(start);
  const tractates = { סנהדרין:'Sanhedrin', שבת:'Shabbat', ברכות:'Berakhot', סוכה:'Sukkah', יבמות:'Yevamot', נדה:'Niddah', גטין:'Gittin', גיטין:'Gittin', פסחים:'Pesachim', מגילה:'Megillah', יומא:'Yoma', קידושין:'Kiddushin' };
  const pattern = new RegExp(`(${Object.keys(tractates).join('|')})\\s+([א-ת"']{1,7}|\\d+)\\s*,?\\s*(?:[סר]?ע["']?([אב])|([אב])(?=[\\s.,;]|$))`, 'g');
  for (const match of text.matchAll(pattern)) {
    const page = numeral(match[2]);
    if (page >= 2 && page < 700) refs.push(`${tractates[match[1]]} ${page}${(match[3] || match[4]) === 'ב' ? 'b' : 'a'}`);
  }
  const anchor = anchorText(source, reader);
  // A bare folio in a Zohar footnote is resolved only with an explicit work
  // in the paragraph and a known volume, not by choosing a random book.
  if (/זוהר|זהר/.test(text + ' ' + anchor)) {
    const volume = text.match(/ח["']([אבג])/)?.[1];
    const page = text.match(/(?:^|\s)([א-ת"']{1,5})\s*,\s*([אב])(?=[\s,.;—–-]|$)/);
    const knownVolume = volume ? numeral(volume) : start.startsWith('Genesis ') ? 1 : 0;
    if (page && knownVolume) refs.push(`Zohar${/תוספתא/.test(text) ? ', Tosefta' : ''} ${knownVolume}:${numeral(page[1])}${page[2] === 'ב' ? 'b' : 'a'}`);
  }
  if (/ב"ר|בראשית\s+רבה/.test(text + ' ' + anchor)) {
    const chapter = text.match(/פ["']([א-ת"']+)\s*,\s*([א-ת"']+)/);
    if (chapter) refs.push(`Bereshit Rabbah ${numeral(chapter[1])}:${numeral(chapter[2])}`);
  }
  return [...new Set(refs)];
}

export function sourceNavigationCandidates(source, sources = [], reader = {}) {
  const contextual = contextualMidrashRef(source, sources);
  const canonical = canonicalCitationRefs(source, reader);
  const values = source.footnote
    ? [...canonical, contextual, source.srcRef, source.citation, source.en, source.he]
    : [source.srcRef, source.citation, ...canonical, contextual, source.title, source.en, source.he];
  return [...new Set(values.map(clean).filter(value => value && value.toLowerCase() !== 'source' && !isInternalSourceId(value) && !/^sheets?(?:\s|$)/i.test(value) && value.length <= 240))];
}
export function isLocatedSource(text) {
  if (!text?.segments?.length) return false;
  const ref = normalize(text.focusRef || text.ref);
  return !!text.focusRef || /\d/.test(ref) && ref !== normalize(text.collection);
}
export function readerOrigin(options, state) {
  return Object.prototype.hasOwnProperty.call(options, 'origin') ? options.origin : state?.mayanosOrigin || null;
}
export function sourceSheetUrl(source) {
  const ref = String(source?.srcRef || source?.citation || source?.ref || '');
  const id = ref.match(/^Sheets?\s+(\d+)(?::\d+)?$/i)?.[1];
  return id ? `https://www.sefaria.org/sheets/${id}` : '';
}
export function verifiedExternalSource(source) {
  const text = clean([source.he, source.en, source.srcRef, source.citation].filter(Boolean).join(' '));
  if (/באתי\s+לגני|Basi\s+Le.?Gani/i.test(text) && /(?:ה'?שי"ת|תש"י|5710)/.test(text) && /רד"ה|פ"א|111/.test(text)) {
    return { url:'https://www.chabad.org/therebbe/article_cdo/aid/115102/jewish/Basi-Legani-5710-Chapter-1.htm', title:'Basi LeGani 5710, Chapter 1', publisher:'Chabad.org' };
  }
  return null;
}

// Once the cited work/folio is known, use a uniquely matching quotation in the
// originating paragraph to land on its segment rather than the top of a page.
export function sourceTargetRef(text, source, reader = {}, requestedRef = '') {
  const anchor = anchorText(source, reader);
  const norm = value => plain(value).normalize('NFKD').replace(/[\u0591-\u05c7\u00ad]/g, '').replace(/[^a-z0-9\u0590-\u05ff]+/gi,' ').toLowerCase().trim();
  for (const match of [...anchor.matchAll(/(?:^|[\s(])["“״]([^"”״]{12,200})["”״](?=[\s,.;:!?)]|$)/g)].reverse()) {
    const quote = norm(match[1]);
    if (quote.split(' ').length < 3) continue;
    const words = quote.split(' ');
    const phrases = [quote];
    // Editions differ slightly (for example כגון / כמו); retain the strongest
    // shared wording while requiring a unique match within the cited page.
    for (let size = Math.min(8, words.length); size >= 3; size--) {
      for (let offset = 0; offset + size <= words.length; offset++) {
        const phrase = words.slice(offset, offset + size).join(' ');
        if (phrase.length >= 14) phrases.push(phrase);
      }
    }
    for (const phrase of phrases) {
      const hits = text.segments.filter(segment => norm(segment.he).includes(phrase) || norm(segment.en).includes(phrase));
      if (hits.length === 1) return hits[0].ref;
    }
  }
  // Mirrors return the containing chapter for a range. The first explicitly
  // cited verse is the landing point when no unique quotation was found.
  const rangeStart = String(requestedRef).replace(/_/g, ' ').match(/^(.*?)[-–]\d+[ab]?(?::\d+)*$/i)?.[1];
  const firstCited = rangeStart && text.segments.find(segment => normalize(segment.ref) === normalize(rangeStart));
  return firstCited?.ref || text.focusRef || (text.segments.length === 1 && text.segments[0].ref) || text.readerRef || text.ref;
}
